import { createFileRoute } from "@tanstack/react-router";
import type { OfflineSnapshot } from "@observe-tech/utils";
import { useRef } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { Loader } from "@/shared/coralreef";
import { LoginPage } from "../pages/Login/LoginPage";
import { useQuickLogin } from "../pages/Login/QuickLogin";
import { useShellHost } from "../shell-handle/ShellHostProvider";
import { createCognitoAuthApi } from "../shell-handle/auth";
import { startEmergencySession } from "../shell-handle/emergencyAuth";
import {
  TIDE_TOKEN_KEY,
  AKVA_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from "../shell-handle/syncedTokens";
import { consumeReturnTo, landingPath } from "../shell-handle/returnTo";
import { broadcastAuthToOtherScreens } from "../shell-handle/desktopAuthSync";
import {
  clearEmergencyMarker,
  matchEmergencyAccount,
  type EmergencyRole,
} from "../exported/shared/utils/emergencySession";

async function syncTokensToLocalStorage() {
  try {
    const s = await fetchAuthSession();
    const id = s.tokens?.idToken?.toString();
    const access = s.tokens?.accessToken?.toString();
    if (id) {
      localStorage.setItem(TIDE_TOKEN_KEY, id);
      localStorage.setItem(AKVA_TOKEN_KEY, id);
    }
    const refresh = access || id;
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } catch {
    /* not signed in */
  }
}

function LoginRoute() {
  const host = useShellHost();
  const auth = createCognitoAuthApi();
  const emergencyRole = useRef<EmergencyRole | null>(null);
  const quickLogin = useQuickLogin();

  const finishLogin = async ({
    emergency = false,
    snapshot,
  }: {
    emergency?: boolean;
    snapshot?: OfflineSnapshot;
  }) => {
    const cloud = !emergency && !snapshot;
    if (snapshot) host.hydrateFromSnapshot(snapshot);
    if (cloud) await syncTokensToLocalStorage();
    await broadcastAuthToOtherScreens();
    if (cloud) await host.loadInitialData();
    // Use full navigation so the tide MFE (mounted on non-shell-owned
    // paths via single-spa) takes over and renders the target page.
    // tanstack-router's typed nav has no `/welcome` route; the splat
    // route '/$' catches it at runtime. A captured deep link (returnTo)
    // wins so shared links resolve to their data view after login.
    window.location.assign(landingPath(emergency, consumeReturnTo()));
  };

  if (quickLogin === undefined) {
    return (
      <div className="flex h-[75vh] w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <LoginPage
      quickLogin={quickLogin}
      onQuickLoginSuccess={(outcome) =>
        finishLogin({
          snapshot: outcome.status === "offline" ? outcome.snapshot : undefined,
        })
      }
      signIn={async (email, password) => {
        clearEmergencyMarker();
        const role = matchEmergencyAccount(email, password);
        emergencyRole.current = role;
        if (!role) return auth.signIn(email, password);
        await startEmergencySession(role, auth);
        return { isSignedIn: true };
      }}
      confirmSignIn={(response) => auth.confirmSignIn(response)}
      isTotpEnrolmentRequired={async () =>
        emergencyRole.current === null && auth.isTotpEnrolmentRequired()
      }
      startTotpSetup={() => auth.startTotpSetup()}
      completeTotpSetup={(code) => auth.completeTotpSetup(code)}
      abandonSignIn={() => auth.abandonSignIn()}
      resetPassword={(username) => auth.resetPassword(username)}
      confirmResetPassword={(username, code, newPassword) =>
        auth.confirmResetPassword(username, code, newPassword)
      }
      onSuccess={() =>
        finishLogin({ emergency: emergencyRole.current !== null })
      }
    />
  );
}

export const Route = createFileRoute("/login")({
  component: LoginRoute,
});
